import { useEffect } from 'react';

import { useAuth } from '@/hooks/use-auth.ts';
import { usePrismic } from '@/hooks/use-prismic.tsx';

import { PrismicDataType } from '@/models/prismic-hook-types.ts';

import { useConfigStore } from '@/stores/config-store.ts';
import { usePrismicStore } from '@/stores/prismic-store.ts';

import {
    buildEnrichedCustomers,
    filterCustomersForUser,
    populateInstanceConfigValues,
    resolveDefaultSelection,
} from '@/utils/customer-utils.ts';

export const useCustomers = (): void => {
    const { authUser, customerIds, instanceIds, groups, isAdmin } = useAuth();
    const { selectedLocation, selectedCustomer, userCustomers } = useConfigStore();
    const { data: customerConfigData } = usePrismic(PrismicDataType.CUSTOMER_CONFIG);
    const { data: locationData } = usePrismic(PrismicDataType.HUB);
    const { data: dcInstanceConfigData } = usePrismic(PrismicDataType.CONFIG_DC_INSTANCE);

    const customers = usePrismicStore((state) => state.customers);

    useEffect(() => {
        if (!customerConfigData || !locationData || !dcInstanceConfigData) return;

        const { customers: enrichedCustomers, instanceColors } = buildEnrichedCustomers(
            customerConfigData,
            locationData,
            dcInstanceConfigData
        );

        usePrismicStore.setState({ customers: enrichedCustomers, instance_colors: instanceColors });
    }, [customerConfigData, locationData, dcInstanceConfigData]);

    useEffect(() => {
        if (!authUser || customers.length === 0) return;

        if (isAdmin) {
            useConfigStore.setState({ userCustomers: populateInstanceConfigValues(customers) });
            return;
        }

        const filteredCustomers = filterCustomersForUser(
            customers,
            new Set<string>(customerIds),
            new Set<string>(instanceIds),
            new Set(groups)
        );

        useConfigStore.setState({ userCustomers: populateInstanceConfigValues(filteredCustomers) });
    }, [authUser, customers, isAdmin, customerIds, instanceIds, groups]);

    useEffect(() => {
        if (!authUser || userCustomers.length === 0) return;

        const selection = resolveDefaultSelection(userCustomers, selectedCustomer, selectedLocation);
        if (!selection) return;

        useConfigStore.setState({
            selectedCustomer: selection.selectedCustomer,
            selectedLocation: selection.selectedLocation,
        });
    }, [authUser, userCustomers, selectedCustomer, selectedLocation]);
};
